import { apiListSensors } from "apis/SensorApi";
import { apiCreateUserDeviceGroups, apiGetUserDeviceGroups, apiUpdateUserDeviceGroups } from "apis/UserDeviceGroups";
import Pagination from "components/Pagination";
import { CURRENT_PAGE_DEFAULT, ITEM_NUM_PER_PAGE_CAMERA_SENSOR } from "constants/config";
import {
  DIALOG_ERROR_GENERAL_ERROR_MESSAGE,
  HEADER_ADDRESS,
  HEADER_COORDINATES,
  HEADER_DIRECTION,
  HEADER_DISPLAY_NAME,
  HEADER_DISPLAY_PATTERN_NAME,
  HEADER_SPLIT_DISPLAY_PATTERN,
  HEADER_MANAGEMENT_AREA,
  HEADER_DISTRICT,
  HEADER_MANAGEMENT_ID,
  HEADER_STREETLIGHT_HEIGHT,
  TEXT_GROUP_NAME_DUPLICATE,
  TEXT_GROUP_NAME_REQUIRED,
  TEXT_SAVE,
  TEXT_SAVE_FAILURE,
  TEXT_SAVE_SUCCESS,
} from "constants/message";
import { useDialog } from "context/DialogContext";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { standardizeString, truncateToThreeDecimals } from "utils/function";
import "./ManagedDeviceList.css";
import { useGeneralSetting } from "context/GeneralSettingContext";
import { apiGetUserByEmail } from "apis/UserApi";
import { AccountType } from "models";
import { useAuth } from "context/AuthContext";

const patternNumbers = [1, 2, 3, 4, 5];

const ManagedDeviceList = ({ keyword, type, currentUser, itemNumPerPage = ITEM_NUM_PER_PAGE_CAMERA_SENSOR }) => {
  const navigate = useNavigate();
  const [sensors, setSensors] = useState([]);
  const { generalSetting } = useGeneralSetting();
  const { error, success } = useDialog();
  const { currentUser: loggedInUser } = useAuth();
  const [curPage, setCurPage] = useState(CURRENT_PAGE_DEFAULT);
  const [userArea, setUserArea] = useState('');
  const [userSensorIds, setUserSensorIds] = useState([]);
  const [userDeviceGroups, setUserDeviceGroups] = useState(null);

  const getAreaName = (areaId) => {
    const area = generalSetting?.borderSettings?.find(border => border.id.toString() === areaId);
    return area?.location?.displayName?.split(',')[0] || '';
  };

  // Direction mapping from camera direction values to display text
  const directionDisplayMap = {
    'NORTH': '北',
    'NORTHEAST': '北東',
    'EAST': '東',
    'SOUTHEAST': '南東',
    'SOUTH': '南',
    'SOUTHWEST': '南西',
    'WEST': '西',
    'NORTHWEST': '北西',
    'INBOUND': '内回り',
    'OUTBOUND': '外回り',
    '': ''
  };

  const [editedSensors, setEditedSensors] = useState([]);
  const [filteredSensors, setFilteredSensors] = useState([]);
  const [patternNames, setPatternNames] = useState([
    "Group1",
    "Group2",
    "Group3",
    "Group4",
    "Group5",
  ]);
  const [checkboxStates, setCheckboxStates] = useState({});

  // Fetch user's sensor IDs and device groups
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (!currentUser) return;
        const user = await apiGetUserByEmail(currentUser.email);
        const userDataObj = user.data || {};
        const sensorIds = userDataObj.nameList || [];

        // Fetch user device groups
        let deviceGroups = null;
        if (user.id) {
          const result = await apiGetUserDeviceGroups(user.id);
          if (result.success && result.data) {
            deviceGroups = result.data;

            // Update pattern names from user device groups
            if (deviceGroups) {
              const newPatternNames = [
                deviceGroups.group1?.name || "Group1",
                deviceGroups.group2?.name || "Group2",
                deviceGroups.group3?.name || "Group3",
                deviceGroups.group4?.name || "Group4",
                deviceGroups.group5?.name || "Group5"
              ];
              setPatternNames(newPatternNames);
            }
          }
        }
        setUserArea(getAreaName(user?.data?.area));
        setUserSensorIds(sensorIds);
        setUserDeviceGroups(deviceGroups);
      } catch (err) {
        error(DIALOG_ERROR_GENERAL_ERROR_MESSAGE);
      }
    };

    const fetchSensors = async () => {
      const sensorsResult = await apiListSensors();
      if (sensorsResult.success && sensorsResult.data && sensorsResult.data.items) {
        const sensorsData = sensorsResult.data.items;
        setSensors(sensorsData);
      } else {
        error(DIALOG_ERROR_GENERAL_ERROR_MESSAGE);
      }
    };

    fetchSensors();
    fetchUserData();
  }, [JSON.stringify(currentUser), error]);

  // Process sensors with directions
  useEffect(() => {
    // Skip processing if sensors are not loaded yet
    if (!sensors.length) {
      return;
    }

    // Only process sensors that are in the user's list
    const userSensors = sensors.filter(sensor =>
      userSensorIds.includes(sensor.id)
    );

    const updatedSensors = userSensors.map((sensor) => {
      const isDouble = sensor.data?.typeCamera === 'DOUBLE';
      const isSwap = sensor.data?.swapImageCamera || false;

      // Get camera directions from sensor data
      const camera1Dir = sensor.data?.camera1Direction || '';
      const camera2Dir = isDouble ? (sensor.data?.camera2Direction || '') : '';

      // Create directions object with display text and pattern data
      const directions = {};

      // Always add two directions with different keys
      const dir1 = directionDisplayMap[camera1Dir] || ' ';
      const dir2 = isDouble && camera2Dir ? directionDisplayMap[camera2Dir] || '  ' : '  ';

      // Initialize patterns based on userDeviceGroups
      const dir1Patterns = [];
      const dir2Patterns = [];

      // Check if device is in any group
      if (userDeviceGroups) {
        patternNumbers.forEach(num => {
          const groupKey = `group${num}`;
          if (userDeviceGroups[groupKey] && userDeviceGroups[groupKey].devices) {
            const deviceInGroup = userDeviceGroups[groupKey].devices.find(device => device.id === sensor.id);
            if (deviceInGroup) {
              // If device is in group, add pattern to first direction
              if ((isDouble && deviceInGroup.directions?.includes(camera1Dir)) || !isDouble) {
                dir1Patterns.push(num);
              }
              // If double camera and device has directions, add pattern to second direction
              if (isDouble && deviceInGroup.directions?.includes(camera2Dir)) {
                dir2Patterns.push(num);
              }
            }
          }
        });
      } else {
        // Default behavior if no userDeviceGroups
        dir1Patterns.push(1);
        if (isDouble) dir2Patterns.push(1);
      }

      directions[dir1] = dir1Patterns;
      directions[dir2] = dir2Patterns;

      // Mark if second direction is empty (for hiding checkboxes)
      const isSecondDirectionEmpty = !(isDouble && camera2Dir);
      return {
        ...sensor,
        directions: directions || {}, // Ensure directions is never null/undefined
        isSwap,
        isSecondDirectionEmpty,
      };
    });

    setEditedSensors(updatedSensors);
  }, [sensors, userSensorIds, userDeviceGroups]);

  useEffect(() => {
    // Sort sensors by managementId
    const sortedSensors = [...editedSensors].sort((a, b) => {
      // Convert to numbers if possible for numeric sorting
      const idA = a.managementId ? (isNaN(Number(a.managementId)) ? a.managementId : Number(a.managementId)) : '';
      const idB = b.managementId ? (isNaN(Number(b.managementId)) ? b.managementId : Number(b.managementId)) : '';
      return idA > idB ? 1 : idA < idB ? -1 : 0;
    });

    if (!keyword) {
      setFilteredSensors(sortedSensors);
      return;
    }

    // Split keyword into multiple keywords by spaces and standardize them
    const keywords = keyword.trim().split(/\s+/).map(k => standardizeString(k)).filter(k => k !== '');

    const searchResult = sortedSensors.filter((sensor) => {
      // Build comprehensive search string with all sensor fields
      const sensorInfo = standardizeString(sensor.managementId) + ' ' +
        standardizeString(sensor.name) + ' ' +
        standardizeString(sensor.location) + ' ' +
        standardizeString(sensor.displayName)

      // Check if all keywords are present in the sensor info
      let accepted = true;
      for (const kw of keywords) {
        if (!sensorInfo.includes(kw)) {
          accepted = false;
          break;
        }
      }
      return accepted;
    });
    setFilteredSensors(searchResult);
  }, [editedSensors, keyword]);

  const getPatternCount = (patternNum) => {
    let count = 0;
    filteredSensors.forEach(sensor => {
      // Check if sensor.directions exists before calling Object.values
      if (sensor.directions) {
        Object.entries(sensor.directions).forEach(([dir, patterns]) => {
          const checkboxKey = `${sensor.id}-${dir}-${patternNum}`;
          const isChecked = checkboxStates[checkboxKey] ?? (patterns && patterns.includes(patternNum));
          if (isChecked) {
            count++;
          }
        });
      }
    });
    return count;
  };

  const handleCheckboxChange = (sensorId, direction, pattern, isChecked) => {
    const checkboxKey = `${sensorId}-${direction}-${pattern}`;
    setCheckboxStates(prev => ({ ...prev, [checkboxKey]: isChecked }));

    const newSensors = editedSensors.map((sensor) => {
      if (sensor.id === sensorId) {
        const patterns = sensor.directions[direction] || [];
        const newPatterns = isChecked
          ? [...patterns, pattern]
          : patterns.filter((p) => p !== pattern);
        return {
          ...sensor,
          directions: { ...sensor.directions, [direction]: newPatterns },
        };
      }
      return sensor;
    });
    setEditedSensors(newSensors);
  };

  const validateSave = () => {
    // Check for blank group names with checkboxes
    for (let i = 0; i < patternNames.length; i++) {
      const patternName = patternNames[i];
      const patternNum = i + 1;

      // If group name is blank, check if any checkboxes are checked for this pattern
      if (!patternName.trim()) {
        // Count checkboxes for this pattern
        const checkboxCount = filteredSensors.reduce((count, sensor) => {
          if (!sensor.directions) return count;
          return count + Object.values(sensor.directions).reduce((dirCount, patterns) => {
            return dirCount + (patterns && patterns.includes(patternNum) ? 1 : 0);
          }, 0);
        }, 0);

        // If there are checked boxes but no group name, show error
        if (checkboxCount > 0) {
          error(TEXT_GROUP_NAME_REQUIRED);
          return false;
        }
      }
    }

    // Check for duplicate group names
    const nonEmptyNames = patternNames.filter(name => name.trim() !== '');
    const uniqueNames = new Set(nonEmptyNames);
    if (uniqueNames.size < nonEmptyNames.length) {
      error(TEXT_GROUP_NAME_DUPLICATE);
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    // Validate before saving
    if (!validateSave()) {
      return;
    }

    try {
      // Prepare groups data with names and devices
      const groupsData = {
        userId: currentUser.id,
        group1: { name: patternNames[0], devices: prepareGroupDevices(1) },
        group2: { name: patternNames[1], devices: prepareGroupDevices(2) },
        group3: { name: patternNames[2], devices: prepareGroupDevices(3) },
        group4: { name: patternNames[3], devices: prepareGroupDevices(4) },
        group5: { name: patternNames[4], devices: prepareGroupDevices(5) }
      };

      // Create or update user device groups
      if (!userDeviceGroups) {
        // Create new user device groups if it doesn't exist
        await apiCreateUserDeviceGroups(groupsData);
      } else {
        // Update existing user device groups
        await apiUpdateUserDeviceGroups(groupsData);
      }
      success(TEXT_SAVE_SUCCESS);
    } catch (err) {
      console.error("Save error:", err);
      error(TEXT_SAVE_FAILURE);
    }
  };

  // Handle navigation to modify-sensor page
  const handleEditSensor = (sensorId) => {
    navigate(`/base/${sensorId}`);
  };

  const handleRowClick = (sensorId) => {
    if (loggedInUser?.accountType !== AccountType.ADMIN) {
      return;
    }
    navigate(`/modify-sensor/${sensorId}`);
  };

  // Prepare devices data for a specific group
  const prepareGroupDevices = (groupNum) => {
    const devices = [];

    // Loop through all sensors to find ones with this pattern
    editedSensors.forEach(sensor => {
      if (!sensor.directions) return;

      // Check if any direction has this pattern
      let hasPattern = false;
      const directionCodes = [];

      // Get original direction codes from sensor data
      const camera1Dir = sensor.data?.camera1Direction || '';
      const camera2Dir = sensor.data?.typeCamera === 'DOUBLE' ? (sensor.data?.camera2Direction || '') : '';

      // Check each display direction for patterns
      Object.entries(sensor.directions).forEach(([displayDir, patterns]) => {
        if (patterns && patterns.includes(groupNum)) {
          hasPattern = true;

          // Get the actual direction code
          let dirCode = '';
          if (displayDir === directionDisplayMap[camera1Dir]) {
            dirCode = camera1Dir;
          } else if (displayDir === directionDisplayMap[camera2Dir]) {
            dirCode = camera2Dir;
          }

          if (dirCode && !directionCodes.includes(dirCode)) {
            directionCodes.push(dirCode);
          }
        }
      });

      // Add device if it has this pattern, even if no directions
      if (hasPattern) {
        devices.push({
          id: sensor.id,
          directions: directionCodes
        });
      }
    });

    return devices;
  };

  const pageNum = !filteredSensors.length ? 1 : Math.ceil(filteredSensors.length / itemNumPerPage);
  // const paginatedSensors = type === "USER" ? filteredSensors : filteredSensors.slice((curPage - 1) * itemNumPerPage, (curPage - 1) * itemNumPerPage + itemNumPerPage);

  return (
    <div className="managed-device-container">
      <div className="base_list_container">
        {/* {type !== "USER" && (
          <Pagination
            pageNum={pageNum}
            curPage={curPage}
            onPageChange={setCurPage}
            itemNumPerPage={itemNumPerPage}
            total={filteredSensors.length}
          />
        )} */}
        <div className="base_list_list_items_container" style={{ height: pageNum <= 1 ? undefined : 'calc(100% - 24px)' }}>
          <div className="base_list_list_items_subcontainer">
            <div className="table-responsive-wrapper">
              <table>
                <colgroup>
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "7%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "10%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th rowSpan="2">{HEADER_MANAGEMENT_ID}</th>
                    <th rowSpan="2">{HEADER_MANAGEMENT_AREA}</th>
                    <th rowSpan="2">{HEADER_DISPLAY_NAME}</th>
                    <th rowSpan="2">{HEADER_ADDRESS}</th>
                    <th rowSpan="2">{HEADER_COORDINATES}</th>
                    <th rowSpan="2">{HEADER_STREETLIGHT_HEIGHT}</th>
                    <th rowSpan="2">{HEADER_DIRECTION}</th>
                    <th colSpan="5">{type === "USER" ? HEADER_SPLIT_DISPLAY_PATTERN : HEADER_DISPLAY_PATTERN_NAME}</th>
                  </tr>
                  <tr>
                    {patternNumbers.map((num, index) => (
                      <th key={num}>
                        <div className="pattern-name-container">
                          <input
                            type="text"
                            value={patternNames[index]}
                            onChange={(e) => {
                              const newNames = [...patternNames];
                              newNames[index] = e.target.value;
                              setPatternNames(newNames);
                            }}
                            className="pattern-name-input group-name-input"
                          />
                          <div className="pattern-count">{`${getPatternCount(num)}台`}</div>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* {paginatedSensors.map((entry) => { */}
                  {filteredSensors.map((entry) => {
                    if (!entry.directions) return null;

                    const directionEntries = Object.entries(entry.directions);
                    const isDouble = entry.data?.typeCamera === 'DOUBLE';
                    const orderedDirections = (entry.isSwap && isDouble) ? [...directionEntries].reverse() : directionEntries;
                    const totalDirections = orderedDirections.length;

                    return orderedDirections.map(([dir, patterns], index) => (
                      <tr
                        key={`${entry.id}-${dir}-${index}`}
                        onClick={() => handleRowClick(entry.id)}
                        className={`device-entry-row ${index === 0 ? 'first-row' : ''}`}
                        onMouseEnter={() => {
                          const sensorRows = document.querySelectorAll(`tr[data-sensor-id="${entry.id}"]`);
                          sensorRows.forEach(row => row.classList.add('hover-group'));
                        }}
                        onMouseLeave={() => {
                          const sensorRows = document.querySelectorAll(`tr[data-sensor-id="${entry.id}"]`);
                          sensorRows.forEach(row => row.classList.remove('hover-group'));
                        }}
                        data-sensor-id={entry.id}
                      >
                        {index === 0 && (
                          <>
                            <td rowSpan={totalDirections}>
                              <a href="#" onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleEditSensor(entry.id);
                              }}>
                                {entry.managementId}
                              </a>
                            </td>
                            <td rowSpan={totalDirections}>
                              {entry.data?.suburb}
                            </td>
                            <td rowSpan={totalDirections}>
                              {entry.name}
                            </td>
                            <td rowSpan={totalDirections}>
                              {entry.location}
                            </td>
                            <td rowSpan={totalDirections}>
                              {`${truncateToThreeDecimals(entry.lat).toFixed(3)}, ${truncateToThreeDecimals(entry.lng).toFixed(3)}`}
                            </td>
                            <td rowSpan={totalDirections}>
                              {entry.data?.streetlightHeight || ''}
                            </td>
                          </>
                        )}
                        <td>{dir}</td>
                        {patternNumbers.map((num) => (
                          <td key={`${entry.id}-${dir}-${num}`}>
                            {(index === 0 || !entry.isSecondDirectionEmpty) && (
                              <input
                                type="checkbox"
                                checked={checkboxStates[`${entry.id}-${dir}-${num}`] ?? (patterns && patterns.includes(num))}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleCheckboxChange(entry.id, dir, num, e.target.checked);
                                }}
                              />
                            )}
                            {!(index === 0 || !entry.isSecondDirectionEmpty) && (
                              <span className="checkbox-placeholder"></span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ));
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <button onClick={handleSave}>{TEXT_SAVE}</button>
    </div>
  );
};

export default ManagedDeviceList;